import React, { useEffect, useCallback } from 'react';
import { useGlobalAction } from '@gadgetinc/react';
import { api } from '../api';

const ResumeConversationModal = ({
  isOpen,
  onClose,
  onNewConversation,
  onResumeConversation,
  agentType,
  agentName
}) => {
  const [{ data, fetching: loading, error }, getAgentConversationHistory] = useGlobalAction(api.getAgentConversationHistory);

  // Format relative time
  const formatRelativeTime = useCallback((dateString) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffMs = now - date;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffHours < 1) {
      return 'Just now';
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString();
    }
  }, []);

  // Fetch conversations when modal opens
  useEffect(() => {
    if (isOpen && agentType) {
      getAgentConversationHistory({
        agentType,
        limit: 20
      });
    }
  }, [isOpen, agentType, getAgentConversationHistory]);

  // Extract conversations from the response data
  const conversations = data?.result?.conversations || [];
  const errorMessage = error ? 'Failed to load conversation history' : null;

  // Handle ESC key press
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
      return () => document.removeEventListener('keydown', handleEscapeKey);
    }
  }, [isOpen, onClose]);

  // Handle backdrop click
  const handleBackdropClick = useCallback((event) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  }, [onClose]);

  // Truncate text helper
  const truncateText = useCallback((text, maxLength = 100) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  }, []);

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px'
      }}
      onClick={handleBackdropClick}
    >
      <div
        style={{
          backgroundColor: '#1a1a1a',
          borderRadius: '12px',
          width: '100%',
          maxWidth: '600px',
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          border: '1px solid #333',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.3)',
          animation: 'modalEnter 0.2s ease-out'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: '24px 24px 0 24px',
            borderBottom: '1px solid #333',
            marginBottom: '20px'
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '16px'
            }}
          >
            <h2
              style={{
                margin: 0,
                color: '#ffffff',
                fontSize: '24px',
                fontWeight: '600'
              }}
            >
              Resume Conversation
            </h2>
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                color: '#888',
                fontSize: '24px',
                cursor: 'pointer',
                padding: '4px',
                borderRadius: '4px',
                transition: 'color 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.color = '#fff'}
              onMouseLeave={(e) => e.target.style.color = '#888'}
            >
              ×
            </button>
          </div>
          <p
            style={{
              margin: 0,
              color: '#888',
              fontSize: '14px',
              paddingBottom: '20px'
            }}
          >
            Continue a previous conversation with {agentName} or start fresh.
          </p>
        </div>

        {/* Content */}
        <div
          style={{
            padding: '0 24px 24px 24px',
            flex: 1,
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px'
          }}
        >
          {/* Start New Conversation Button */}
          <button
            onClick={onNewConversation}
            style={{
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: '12px 16px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#2563eb';
              e.target.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#3b82f6';
              e.target.style.transform = 'translateY(0)';
            }}
          >
            <span>+</span>
            Start New Conversation
          </button>

          {/* Loading State */}
          {loading && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '40px',
                color: '#888'
              }}
            >
              <div
                style={{
                  width: '24px',
                  height: '24px',
                  border: '2px solid #333',
                  borderTop: '2px solid #3b82f6',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}
              />
              <span style={{ marginLeft: '12px' }}>Loading conversations...</span>
            </div>
          )}

          {/* Error State */}
          {errorMessage && (
            <div
              style={{
                padding: '20px',
                backgroundColor: '#2d1b1b',
                color: '#ef4444',
                borderRadius: '8px',
                border: '1px solid #3d1b1b',
                textAlign: 'center'
              }}
            >
              {errorMessage}
            </div>
          )}

          {/* Empty State */}
          {!loading && !errorMessage && conversations.length === 0 && (
            <div
              style={{
                textAlign: 'center',
                padding: '40px 20px',
                color: '#888'
              }}
            >
              <div
                style={{
                  fontSize: '48px',
                  marginBottom: '16px',
                  opacity: 0.5
                }}
              >
                💬
              </div>
              <h3
                style={{
                  margin: '0 0 8px 0',
                  color: '#ccc',
                  fontSize: '18px'
                }}
              >
                No Previous Conversations
              </h3>
              <p
                style={{
                  margin: 0,
                  fontSize: '14px'
                }}
              >
                Start your first conversation with {agentName}
              </p>
            </div>
          )}

          {/* Conversation List */}
          {!loading && conversations.length > 0 && (
            <>
              <div
                style={{
                  color: '#888',
                  fontSize: '14px',
                  fontWeight: '500',
                  marginTop: '8px',
                  marginBottom: '8px'
                }}
              >
                Recent Conversations ({conversations.length})
              </div>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {conversations.map((conversation, index) => (
                  <div
                    key={conversation.localSessionId || index}
                    style={{
                      backgroundColor: '#2a2a2a',
                      borderRadius: '8px',
                      padding: '16px',
                      border: '1px solid #333',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#333';
                      e.currentTarget.style.borderColor = '#444';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#2a2a2a';
                      e.currentTarget.style.borderColor = '#333';
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginBottom: '12px'
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <h4
                          style={{
                            margin: '0 0 4px 0',
                            color: '#fff',
                            fontSize: '16px',
                            fontWeight: '500'
                          }}
                        >
                          {conversation.title || `${agentName} Conversation`}
                        </h4>
                        <div
                          style={{
                            display: 'flex',
                            gap: '12px',
                            fontSize: '12px',
                            color: '#888'
                          }}
                        >
                          <span>{formatRelativeTime(conversation.lastActivity)}</span>
                          <span>•</span>
                          <span>{conversation.messageCount || 0} messages</span>
                        </div>
                      </div>
                      <button
                        onClick={() => onResumeConversation(conversation.localSessionId)}
                        style={{
                          backgroundColor: 'transparent',
                          color: '#3b82f6',
                          border: '1px solid #3b82f6',
                          borderRadius: '6px',
                          padding: '6px 12px',
                          fontSize: '12px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          marginLeft: '12px',
                          flexShrink: 0
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#3b82f6';
                          e.target.style.color = 'white';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = 'transparent';
                          e.target.style.color = '#3b82f6';
                        }}
                      >
                        Resume
                      </button>
                    </div>
                    
                    {conversation.summary && (
                      <p
                        style={{
                          margin: 0,
                          color: '#ccc',
                          fontSize: '14px',
                          lineHeight: '1.4'
                        }}
                      >
                        {truncateText(conversation.summary, 120)}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* CSS animations */}
      <style jsx>{`
        @keyframes modalEnter {
          from {
            opacity: 0;
            transform: scale(0.9) translateY(-10px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }

        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        /* Responsive styles */
        @media (max-width: 640px) {
          .modal-content {
            margin: 10px;
            max-height: 90vh;
          }
        }
      `}</style>
    </div>
  );
};

export default ResumeConversationModal;